import { cn } from "@/lib/utils";
import {
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineUser,
} from "react-icons/hi2";
import { NotAvailable } from "../search/SearchCard";
import {
  formatDate2,
  formatDistanceFromNow,
  formatPhoneNumber,
  isDateInFuture,
} from "../../utils/helpers";
import { Separator } from "../../components/ui/separator";
import { useContext } from "react";
import { ContractContext } from "./ContractDescription";
import ThemedSkeleton from "@/ui/ThemedSkeleton";

import {
  Timeline,
  TimelineItem,
  TimelineTitle,
  TimelineDescription,
  TimelineTime,
  TimelineHeader,
} from "@/components/ui/timeline";

export function EmailLink({ link }) {
  if (!link) return null;
  return (
    <a
      href={`mailto:${link}`}
      className="text-blue-500 cursor-pointer hover:underline"
    >
      {link}
    </a>
  );
}

export function DetailTitle({ title, icon: Icon, className }) {
  return (
    <span
      className={cn("flex text-lg font-bold gap-3 items-center", className)}
    >
      {Icon && (
        <span className="bg-muted/70 rounded-md p-1">
          <Icon className="text-primary/70 h-5 w-5 " />
        </span>
      )}
      {title}
    </span>
  );
}
const isValid = (detail) =>
  detail !== null && detail !== undefined && detail !== "nan" && detail !== "";

export function Detail({ title, icon, details }) {
  const { isLoading } = useContext(ContractContext);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1">
        <DetailTitle title={title} icon={icon} />
        <ThemedSkeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (details.length === 0) return null;
  if (!details.some(isValid)) return null;
  return (
    <div className="flex flex-col gap-1">
      <DetailTitle title={title} icon={icon} />
      <dd className="flex flex-col gap-1">
        {isLoading ? (
          <ThemedSkeleton className="h-4 w-1/2" />
        ) : (
          details.filter(isValid).map((detail, index) => (
            <div key={index} className="ml-8">
              {detail}
            </div>
          ))
        )}
      </dd>
    </div>
  );
}

export function ContactDetail({ title, icon, fullname, email, phone }) {
  const { isLoading } = useContext(ContractContext);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <DetailTitle title={title} icon={icon} />
        <ThemedSkeleton className="h-4 w-1/2" />
        <ThemedSkeleton className="h-4 w-1/2" />
        <ThemedSkeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (![fullname, email, phone].some(isValid)) return null;
  return (
    <div>
      <DetailTitle title={title} icon={icon} />
      <dd className="flex flex-col gap-1">
        {isValid(fullname) && (
          <div className="ml-8 flex gap-1 items-center">
            <HiOutlineUser />
            &nbsp;{fullname}
          </div>
        )}
        {isValid(phone) && (
          <div className="ml-8 flex gap-1 items-center">
            <HiOutlinePhone />
            &nbsp;{formatPhoneNumber(phone)}
          </div>
        )}
        {isValid(email) && (
          <div className="ml-8 flex gap-1 items-center">
            <HiOutlineEnvelope />
            &nbsp; <EmailLink link={email} />
          </div>
        )}
      </dd>
    </div>
  );
}

export function DateDetails({
  postedDate,
  lastUpdatedDate,
  dueDate,
  isLoading,
  className,
  ...props
}) {
  const load = (value) =>
    isLoading ? (
      <ThemedSkeleton className="h-4 w-1/2" />
    ) : (
      value || <NotAvailable />
    );
  return (
    <div
      className={cn(
        "grid grid-rows-[3rem_2ps_1fr_2px_1fr] grid-cols-1 gap-3 pl-6 border-l",
        className
      )}
      {...props}
    >
      <Separator className="bg-muted/70" />

      <div className="flex flex-col gap-1">
        <div className="text-muted-foreground row-span-2 justify">Posted</div>
        <div>
          <div> {load(formatDate2(postedDate))}</div>
          <div className="text-accent-foreground text-sm ">
            {load(formatDistanceFromNow(postedDate))}
          </div>
        </div>
      </div>
      {lastUpdatedDate && (
        <>
          <Separator className="bg-muted/70" />

          <div className="flex flex-col gap-1">
            <div className="text-muted-foreground row-span-2 justify">
              Last Updated
            </div>
            <div>
              <div> {load(formatDate2(postedDate))}</div>
              <div className="text-accent-foreground text-sm ">
                {load(formatDistanceFromNow(postedDate))}
              </div>
            </div>
          </div>
        </>
      )}
      <Separator className="bg-muted/70" />
      <div className="flex flex-col gap-1">
        <div className="text-muted-foreground row-span-2 justify">Due</div>
        <div>
          <div> {load(formatDate2(dueDate))}</div>
          <div className="text-accent-foreground text-sm">
            {load(isDateInFuture(dueDate)) ? (
              load(formatDistanceFromNow(dueDate))
            ) : (
              <span className="text-destructive">Past Due</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function DateTimeline({ dates, isLoading, className, ...props }) {
  const load = (value) =>
    isLoading ? (
      <ThemedSkeleton className="h-4 w-32" />
    ) : (
      value || <NotAvailable />
    );
  const defaultDates = [{ title: "Posted" }, { title: "Due Date" }];
  const processedDates =
    dates
      .filter((item) => formatDate2(item.date))
      .sort((a, b) => new Date(a.date) - new Date(b.date)) || defaultDates;

  const isDueDatePassed = (item) =>
    item.title === "Due Date" && isDateInFuture(item.date) === false;
  return (
    <Timeline
      className={cn(
        "justify-self-center row-span-2 row-start-1 col-start-3",
        className
      )}
      {...props}
    >
      {processedDates.map((item, i) => (
        <TimelineItem key={i}>
          <TimelineHeader
            className={`${
              isDateInFuture(item.date) === false
                ? "after:border-primary before:bg-[var(--sky-200)]"
                : "after:border-[var(--gray-500)]"
            }   after:bg-background ${
              isDueDatePassed(item) && "after:border-destructive/70"
            }`}
          >
            <TimelineTitle>{item.title}</TimelineTitle>
            <TimelineTime>{load(formatDate2(item.date))}</TimelineTime>
          </TimelineHeader>
          <TimelineDescription className="flex flex-col">
            {isDueDatePassed(item) && (
              <span className="text-destructive">Past Due</span>
            )}
            {formatDistanceFromNow(item.date)}
          </TimelineDescription>
        </TimelineItem>
      ))}
    </Timeline>
  );
}
export function ContractHeader({ headerData, timelineData }) {
  const { isLoading } = useContext(ContractContext);
  return (
    <div className="grid grid-cols-3">
      <div className="col-span-2 grid grid-cols-2 gap-y-8 ">
        {headerData.map((item, i) => (
          <Detail key={i} {...item} />
        ))}
      </div>
      <DateTimeline
        className="justify-self-center col-span-1 row-span-10"
        dates={timelineData}
        isLoading={isLoading}
      />
    </div>
  );
}

export const ConditionSeparator = (...conditions) =>
  conditions.some(Boolean) && <Separator />;
